import { useState, useEffect } from 'react';
import { Dialog, DialogContent, Grid, IconButton, Typography, SxProps, Theme, Avatar, Button as MuiButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import Moment from "react-moment";
import { usePlayer } from "../player/playerContext";
import { getReducedFiendlyName, getRoundName } from '../common/RoundHelper';
import { PoolXNumberFormat } from '../common/CurrencyFormatter';
import TerminalSyndicateCoupon from './terminalsyndicatecoupon';
import SyndicateConfirmDialog from './syndicateconfirmdialog';
import { Button } from '../reusable-components';
import { IsLeo, IsVideo } from '../theme/themehelper';
import { operatorAdviceLogin, operatorAdviceScrollToTop } from '../App';
import LoginDialog from '../slimcoupon/logindialog';
import useSyndicate from '../syndicates/useSyndicate';
import useAgents from '../syndicates/useAgents';
import fetchApi from '../backend/fetchApi';
import useDetailRound from '../rounds/useDetailRound';
import { getTeamName } from '../slimcoupon/couponhelpers';
import { Syndicate } from '../models/syndicate';
import { DetailedRound } from '../models/round';

interface SyndicateModalProps {
    open: boolean;
    onClose: () => void;
    syndicateId: number;
    roundId: number;
    purchase: any;
    readonly: boolean;
}

export default function SyndicateModal({ open, onClose, syndicateId, roundId, purchase, readonly }: SyndicateModalProps) {
    const { t } = useTranslation();
    const [showConfirm, setShowConfirm] = useState(false);
    const [player, config] = usePlayer() as any;
    let [showLoginDialog, setShowLoginDialog] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    const syndicateResponse = useSyndicate(syndicateId);
    const agents = useAgents();
    const { round } = useDetailRound(roundId);

    let syndicate = syndicateResponse.syndicate;
    let agent = agents?.find(a => a.id === syndicate?.agentId);

    const fetchImage = async (imageUrl: string) => {
        try {
            const response = await fetchApi(imageUrl);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            return objectUrl;
        } catch (error) {
            console.error("Error fetching image:", error);
            return null;
        }
    };

    useEffect(() => {
        async function loadAvatar() {
            if (agent?._links?.avatarImage?.href) {
                const url = await fetchImage(agent._links.avatarImage.href);
                if (url) {
                    setAvatarUrl(url);
                }
            }
        }

        if (agent) {
            loadAvatar();
        }
    }, [agent]);

    if (syndicateResponse.error || syndicateResponse.syndicate === undefined) {
        return null;
    }

    if (!syndicate || !round) return null;

    let theme = window.theme;
    let showLogin = IsLeo(theme);

    if (IsVideo(theme) && player._links.self.href.indexOf('demo') > -1) {
        showLogin = true;
    }

    const openToDate = new Date(round.openTo);
    let locale = t('locale');

    const nf = PoolXNumberFormat(locale, {
        style: 'currency',
        currency: syndicate.stakePerShare.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    let formattedStakePerShare = nf.format(syndicate.totalAmountPerShare.amount);
    let feePercentage = Math.round((syndicate.agentFeePerShare.amount / syndicate.stakePerShare.amount * 100)) + "%";

    if (!(config.syndicateBetting || config.agentSyndicateBetting)) {
        return null;
    }

    let isRealMode = player && player._embedded && player._embedded.mode === 'real';

    const handleLoginClick = () => {
        setShowLoginDialog(false);
        if (!syndicate) return;
        let loginEvent = {
            type: "login_user",
            navigationId: syndicate.id,
            navigationKey: "syndicate",
            roundId: syndicate.roundId
        }
        operatorAdviceLogin(loginEvent);
    }

    const handlePayClick = () => {
        if (showLogin && !isRealMode) {
            setShowLoginDialog(true);
        } else {
            setShowConfirm(true);
            operatorAdviceScrollToTop(500);
        }
    }

    const handleToggle = (toggle: boolean) => {
        setShowConfirm(toggle);
    }

    let rowStyle: SxProps<Theme> = {
        background: 'var(--background_1)',
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'wrap',
        width: "auto",
        padding: '5px 0px',
        color: '#091F51',
    }

    let textStyle: SxProps<Theme> = {
        fontWeight: 'bold',
        marginLeft: '3px',
    }

    let greenDivColumnStyle: SxProps<Theme> = {
        background: 'var(--background_1)',
        display: 'flex',
        alignItems: 'flex-start',        
        paddingLeft: '10px',
        flexDirection: 'column'
    }

    let titleMarginStyle: SxProps<Theme> = {        
        fontSize: '26px',
        fontWeight: '700',
        color: '#091F51',
    }

    let titleUnderStyle: SxProps<Theme> = {
        fontWeight: '500',
        fontSize: '20px',
        color: '#091F51',
    }

    let isReduced = false;
    let isSingle = false;
    let reducedSystemName = "";

    if ((!syndicate.couponHidden || syndicate.showDraftCoupon) &&
        syndicate.pendingRoundCoupon &&
        syndicate.pendingRoundCoupon.bets &&
        syndicate.pendingRoundCoupon.bets.length > 0) {
        isReduced = syndicate.pendingRoundCoupon.bets[0].system.type === "REDUCED";
        isSingle = syndicate.pendingRoundCoupon.bets.length > 1;
        if (isReduced) {
            reducedSystemName = getReducedFiendlyName(syndicate.pendingRoundCoupon.bets[0].system.reducedSystemId)
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        maxWidth: '1000px',
                        marginTop: '16px',
                        background: 'var(--background_1)',
                    }
                }}
            >
                <Grid container sx={{
                    height: '50px',
                    position: 'relative',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: '8px'
                }}>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'var(--text-black)',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>

                <DialogContent sx={{ padding: 0, overflow: 'auto', maxHeight: 'calc(90vh - 50px)' }}>
                    {showLoginDialog && (
                        <LoginDialog open={showLoginDialog} onClose={() => handleLoginClick()} />
                    )}

                    <Grid container direction="row" sx={{ minHeight: '400px' }}>
                        {/* Left Column - Information */}
                        <Grid item sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: "400px",
                            padding: '0px 10px',                            
                            justifyContent: 'space-between',
                        }}>
                            <Grid sx={{ display: 'flex', flexDirection: 'column'}}>
                                {agent && (
                                    <Grid container sx={{
                                        background: '#0A2259',
                                        padding: '5px',
                                        borderRadius: '563px',                                        
                                        alignItems: 'center',
                                        gap: '15px',
                                        flexWrap: 'nowrap',
                                        marginBottom: '20px'
                                    }}>
                                        {avatarUrl ? (
                                            <Avatar alt={agent.name} src={avatarUrl} sx={{ width: 56, height: 56 }} />
                                        ) : (
                                            <Avatar sx={{ 
                                                width: 56, 
                                                height: 56, 
                                                bgcolor: '#1F4BA8', 
                                                color: '#FFFFFF', 
                                                fontSize: '24px', 
                                                fontWeight: '700' 
                                            }}>
                                                {agent.name?.[0]}
                                            </Avatar>
                                        )}
                                        <Grid sx={{ flexGrow: 1 }}>                                            
                                            <Typography sx={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold',
                                                color: '#FFFFFF'
                                            }}>
                                                {agent.name}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid sx={greenDivColumnStyle}>
                                    <Typography sx={titleMarginStyle}>{syndicate.name}</Typography>
                                    <Typography sx={titleUnderStyle}>
                                        {getRoundName(round, t)} <Moment format={"L"} locale={locale}>{openToDate}</Moment>
                                    </Typography>
                                    {syndicate.agentUserName !== undefined &&
                                        <Typography sx={rowStyle}>{syndicate.agentUserName}</Typography>
                                    }
                                    {syndicate.description &&
                                        <Typography sx={rowStyle}>
                                            {syndicate.description}
                                        </Typography>
                                    }

                                    <Grid container sx={rowStyle}>
                                        <Typography>{t('share_price')}:</Typography>
                                        <Typography sx={textStyle}>{formattedStakePerShare} </Typography>
                                    </Grid>
                                    <Grid container sx={rowStyle}>
                                        <Typography>{t('shares')}:</Typography>
                                        <Typography sx={textStyle}>{syndicate.targetNShares} </Typography>
                                    </Grid>
                                    {!syndicate.nSharesHidden &&
                                        <Grid container sx={rowStyle}>
                                            <Typography>{t('sold_shares')}:</Typography>
                                            <Typography sx={textStyle}>{syndicate.nShares} </Typography>
                                        </Grid>
                                    }
                                    <Grid container sx={rowStyle}>
                                        <Typography>{t('fee')}:</Typography>
                                        <Typography sx={textStyle}>{feePercentage}</Typography>
                                    </Grid>

                                    {isReduced &&
                                        <Grid container direction="row" justifyContent="center">
                                            <Grid container sx={rowStyle}>
                                                <Typography>{t('reduced_system')}:</Typography>
                                                <Typography sx={textStyle}>{reducedSystemName} </Typography>
                                            </Grid>
                                        </Grid>
                                    }
                                    {isSingle &&
                                        <Grid container direction="row" justifyContent="center">
                                            <Grid container sx={rowStyle}>
                                                <Typography>{t('single_rows')}</Typography>
                                            </Grid>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                            
                            <Grid sx={{ padding: '20px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                {!readonly && (
                                    <>
                                        <SyndicateConfirmDialog
                                            open={showConfirm}
                                            onClose={() => handleToggle(false)}
                                            syndicate={syndicate}
                                            purchase={purchase}
                                            parentOnClose={onClose}
                                        />
                                        <Button
                                            style={{ width: '100%' }}
                                            color="#4075FF"
                                            hoverColor="#4075FF"
                                            borderRadius="41px"
                                            disabled={(!isRealMode && !showLogin) || syndicate.nShares === syndicate.targetNShares}
                                            onClick={() => handlePayClick()}>
                                            {t('pay_syndicate') + ' ' + formattedStakePerShare}
                                        </Button>
                                        <MuiButton
                                            variant="outlined"
                                            onClick={() => handlePrintStoreReceipt(syndicate!, round, t)}
                                            sx={{
                                                width: '100%',
                                                marginTop: '10px',
                                                borderColor: '#112F7C',
                                                color: '#112F7C',
                                                fontWeight: '700',
                                                fontSize: '14px',
                                                borderRadius: '41px',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#112F7C',
                                                    color: '#FFFFFF'
                                                }
                                            }}
                                        >
                                            {t('print_store_receipt')}
                                        </MuiButton>
                                    </>
                                )}
                                {readonly && (
                                    <Button
                                        style={{ width: '100%' }}
                                        color="#4075FF"
                                        hoverColor="#4075FF"
                                        borderRadius="41px"
                                        onClick={onClose}>
                                        {t('ok')}
                                    </Button>
                                )}
                            </Grid>

                        </Grid>

                        {/* Right Column - Coupon */}
                        <Grid item sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            padding: '0px 10px 20px 10px'
                        }}>
                            <TerminalSyndicateCoupon syndicate={syndicate} round={round}></TerminalSyndicateCoupon>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
}

function sendToApp(message: object) {
    const json = JSON.stringify(message);
    if ((window as any).Android && (window as any).Android.onMessage) {
        (window as any).Android.onMessage(json);
    }
    // Fallback: post to parent window for cross-origin iframe hosts
    if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, '*');
    }
}

function handlePrintStoreReceipt(syndicate: Syndicate, round: DetailedRound, t: any) {
    const isReduced = syndicate?.pendingRoundCoupon?.bets?.[0]?.system?.type === "REDUCED";
    const isSingleRows = (syndicate?.pendingRoundCoupon?.bets?.length ?? 0) > 1;
    const isPreliminary = !syndicate.isFinal;
    const couponHidden = syndicate.couponHidden && !syndicate.showDraftCoupon;

    const matches = round.fixtures.map((fixture, index) => {
        const home = getTeamName(fixture.homeTeam, t);
        const away = getTeamName(fixture.awayTeam, t);

        let prediction = "";
        let percentages: { "1"?: number; "X"?: number; "2"?: number } | undefined = undefined;

        if (!couponHidden && syndicate.pendingRoundCoupon) {
            if (isSingleRows) {
                const bets = syndicate.pendingRoundCoupon.bets ?? [];
                let count1 = 0, countX = 0, count2 = 0;
                for (const bet of bets) {
                    const signs = bet.legs?.[index]?.signs ?? [];
                    if (signs.indexOf("ONE") > -1) count1++;
                    if (signs.indexOf("X") > -1) countX++;
                    if (signs.indexOf("TWO") > -1) count2++;
                }
                const total = count1 + countX + count2;
                if (total > 0) {
                    percentages = {
                        "1": Math.round((count1 / total) * 100),
                        "X": Math.round((countX / total) * 100),
                        "2": Math.round((count2 / total) * 100),
                    };
                }
                const parts: string[] = [];
                if (count1 > 0) parts.push("1");
                if (countX > 0) parts.push("X");
                if (count2 > 0) parts.push("2");
                prediction = parts.join("");
            } else if (isReduced) {
                const leg = syndicate.pendingRoundCoupon.bets?.[0]?.legs?.[index];
                const signs = leg?.signs ?? [];
                const total = signs.length;
                if (total > 0) {
                    const has1 = signs.indexOf("ONE") > -1;
                    const hasX = signs.indexOf("X") > -1;
                    const has2 = signs.indexOf("TWO") > -1;
                    const pct = 1 / total;
                    percentages = {
                        "1": has1 ? Math.round(pct * 100) : 0,
                        "X": hasX ? Math.round(pct * 100) : 0,
                        "2": has2 ? Math.round(pct * 100) : 0,
                    };
                    const parts: string[] = [];
                    if (has1) parts.push("1");
                    if (hasX) parts.push("X");
                    if (has2) parts.push("2");
                    prediction = parts.join("");
                }
            } else {
                const leg = syndicate.pendingRoundCoupon.bets?.[0]?.legs?.[index];
                const signs = leg?.signs ?? [];
                const parts: string[] = [];
                if (signs.indexOf("ONE") > -1) parts.push("1");
                if (signs.indexOf("X") > -1) parts.push("X");
                if (signs.indexOf("TWO") > -1) parts.push("2");
                prediction = parts.join("");
            }
        }

        return {
            nr: index + 1,
            prediction,
            home,
            away,
            ...(percentages && { percentages }),
        };
    });

    let gameType = "";
    if (round.name.indexOf("Flex") > -1) gameType = "Super 8";
    else if (round.name.indexOf("Mix") > -1) gameType = "Super 12";
    else if (round.name.indexOf("Max") > -1) gameType = "Super 13";
    else if (round.name.indexOf("Hockey9") > -1) gameType = "Hockey 9";
    else if (round.name.indexOf("Hockey7") > -1) gameType = "Hockey 7";

    const stakePerShare = Number(syndicate.stakePerShare?.amount ?? 0);
    const feePerShare = Number(syndicate.agentFeePerShare?.amount ?? 0) + Number(syndicate.operatorFeePerShare?.amount ?? 0);
    const costPerShare = Number(syndicate.totalAmountPerShare?.amount ?? 0);
    const costTotal = costPerShare * syndicate.targetNShares;

    const systemName = syndicate.pendingRoundCoupon?.bets?.[0]?.system?.type === "REDUCED"
        ? syndicate.pendingRoundCoupon?.bets?.[0]?.system?.reducedSystemId ?? ""
        : "";

    const ticketData = {
        operatorId: "DBET",
        syndicateNumber: syndicate.name,
        gameType,
        shares: syndicate.targetNShares,
        round: round.name,
        system: systemName,
        matches,
        serialNumber: syndicate.id.toString(),
        stakePerShare,
        feePerShare,
        costPerShare,
        costTotal,
        ...(isPreliminary && { isPreliminary: true }),
        ...(couponHidden && { couponHidden: true }),
    };

    sendToApp({
        type: "printStoreReceipt",
        ticketData,
    });
}
